import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import { Check, ChevronDown, Info } from "lucide-react";
import type { BuilderFormat } from "campaign-builder/config";
import FormatIcon from "./FormatIcon";
import { getFlyerFormatMeta, formatSizeLabel, type FlyerFormatMeta } from "./formatMeta";

interface FormatSectionProps {
  formats: BuilderFormat[];
  value?: string | null;
  /** Channel name used in the single-format disclaimer, e.g. "Flyering" / "Door to Door". */
  channelName: string;
  /** Account country — drives the size unit (US/CA inches, else mm). */
  countryCode?: string | null;
  disabled?: boolean;
  onSelect: (format: string) => void;
}

// Fixed 60×48 slot so every format glyph aligns in the card; the glyph itself scales
// with the format (FormatIcon).
const FormatGlyph = ({ meta }: { meta: FlyerFormatMeta }) => (
  <div className="flex h-12 w-[60px] shrink-0 items-center justify-center">
    <FormatIcon
      formatWidth={meta.widthMm / 25.4}
      formatHeight={meta.heightMm / 25.4}
      orientation="portrait"
      sizeName={meta.iconSizeName}
      containerSize={48}
    />
  </div>
);

const FormatLine = ({ format, countryCode }: { format?: string | null; countryCode?: string | null }) => {
  const meta = getFlyerFormatMeta(format);
  if (!meta) return <span className="text-muted-foreground text-sm">Select a format</span>;
  return (
    <div className="flex flex-1 items-center gap-3 overflow-hidden">
      <FormatGlyph meta={meta} />
      <div className="flex flex-1 flex-col items-start gap-0.5 overflow-hidden">
        <span className="text-foreground text-sm font-medium leading-5">{meta.label}</span>
        <span className="text-muted-foreground text-xs leading-4">{formatSizeLabel(meta, countryCode)}</span>
      </div>
    </div>
  );
};

// AC37 — a single available format renders read-only with a disclaimer (no selector).
const SingleFormat = ({
  value,
  channelName,
  countryCode,
}: {
  value: string;
  channelName: string;
  countryCode?: string | null;
}) => {
  const meta = getFlyerFormatMeta(value);
  return (
    <div className="flex flex-col gap-2" data-cypress-id="design-format-single">
      <p className="text-foreground text-sm font-medium leading-5">Selected format</p>
      <div className="border-border flex w-full items-center gap-4 rounded-lg border border-solid bg-background p-4">
        <FormatLine format={value} countryCode={countryCode} />
      </div>
      <div className="flex items-center gap-1.5" data-cypress-id="design-format-disclaimer">
        <Info className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
        <p className="text-sm leading-5 text-muted-foreground">
          {`${meta?.label ?? value} is the only format available for ${channelName} campaigns.`}
        </p>
      </div>
    </div>
  );
};

// AC38 — multiple available formats render as a dropdown (ADM pattern).
const FormatDropdown: React.FC<FormatSectionProps> = ({ formats, value, countryCode, disabled, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (format: string) => {
    setOpen(false);
    if (format !== value) onSelect(format);
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-foreground text-sm font-medium leading-5">Selected format</p>
      <div ref={ref} className="relative">
        <button
          type="button"
          disabled={disabled}
          data-cypress-id="design-format-dropdown"
          onClick={() => setOpen((prev) => !prev)}
          className="border-border flex w-full items-center gap-4 rounded-lg border border-solid bg-background p-4 disabled:opacity-60"
        >
          <FormatLine format={value} countryCode={countryCode} />
          <ChevronDown className={cn("text-muted-foreground h-5 w-5 transition-transform", open && "rotate-180")} />
        </button>

        {open && (
          <div className="border-border absolute left-0 right-0 top-full z-30 mt-2 flex flex-col gap-1 rounded-lg border border-solid bg-card p-2 shadow-[0px_8px_24px_rgba(2,6,23,0.12)]">
            {formats.map((format) => {
              const meta = getFlyerFormatMeta(format.value);
              const isSelected = format.value === value;
              return (
                <button
                  key={format.value}
                  type="button"
                  onClick={() => choose(format.value)}
                  data-cypress-id={`design-format-option-${format.value}`}
                  className={cn(
                    "flex items-center gap-3 rounded-lg p-3 text-left",
                    isSelected ? "bg-slate-50 dark:bg-muted" : "hover:bg-slate-50 dark:hover:bg-muted"
                  )}
                >
                  {meta && <FormatGlyph meta={meta} />}
                  <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
                    <span className="text-foreground text-sm font-medium leading-5">{meta?.label ?? format.label}</span>
                    {meta && (
                      <span className="text-muted-foreground text-xs leading-4">
                        {formatSizeLabel(meta, countryCode)}
                      </span>
                    )}
                  </div>
                  {isSelected && <Check className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const FormatSection: React.FC<FormatSectionProps> = (props) => {
  if (props.formats.length <= 1) {
    const single = props.value || props.formats[0]?.value;
    if (!single) return null;
    return <SingleFormat value={single} channelName={props.channelName} countryCode={props.countryCode} />;
  }
  return <FormatDropdown {...props} />;
};
